import React, { forwardRef, useState, useEffect, useRef } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";

const highlightText = (text, query) => {
  if (!query.trim()) return text;

  const regex = new RegExp(
    `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi"
  );
  const parts = text?.split(regex);

  return parts?.map((part, index) =>
    regex.test(part) ? (
      <span key={index} className="text-[#A67C52]">
        {part}
      </span>
    ) : (
      part
    )
  );
};

const SearchInput = forwardRef(
  (
    {
      className,
      value,
      onChange,
      onClear,
      loading,
      showClearButton = true,
      disabled,
      results,
      onSelectResult,
      ...props
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState(value || "");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
      setInternalValue(value || "");
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          setIsDropdownOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    useEffect(() => {
      if (internalValue.length > 0) {
        setIsDropdownOpen(true);
      } 
      else {
        setIsDropdownOpen(false);
      }
    }, [internalValue]);

    const handleClear = () => {
      setInternalValue("");
      if (onClear) {
        onClear();
      }
      // Also trigger onChange with empty value if it's a controlled component
      if (onChange) {
        onChange({ target: { value: "" } });
      }
      setIsDropdownOpen(false); // Close dropdown on clear
    };

    const handleChange = (e) => {
      setInternalValue(e.target.value);
      if (onChange) {
        onChange(e);
      }
    };

    const handleFocus = () => {
      // Open dropdown on focus if there's text and results
      setIsDropdownOpen(internalValue.length > 0 && (results?.length || 0) > 0);
    };

    const handleResultClick = (result) => {
      if (onSelectResult) {
        onSelectResult(result);
      }
      // Update input with selected value (e.g., loan number and description)
      setInternalValue(`${result?.loanId}, ${result?.loanType}, ${result?.propertyAddress}` || "");
      setIsDropdownOpen(false);
    };

    const showClear = showClearButton && internalValue && !disabled;

    // Determine right padding based on presence of clear button and/or loading spinner
    let prClass = "pr-4"; // Default padding
    if (showClear && loading) {
      prClass = "pr-14"; // Space for both clear button and spinner
    } else if (showClear) {
      prClass = "pr-10"; // Space for clear button
    } else if (loading) {
      prClass = "pr-10"; // Space for spinner
    }

    return (
      <div
        className={cn("relative w-full max-w-md", className)}
        ref={containerRef}
      >
        <div className="relative flex items-center">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            ref={ref}
            className={cn(
              "w-full pl-10 pr-10 py-2 text-sm text-[#333] bg-white border border-[#E5E5E5] hover:shadow-md rounded-lg focus:outline-none focus:border-[#262626] focus:shadow-sm transition-colors",
              prClass
            )} // Apply dynamic padding
            value={internalValue}
            onChange={handleChange}
            onFocus={handleFocus}
            disabled={disabled} // Only disable if explicitly passed as disabled
            autoComplete="off"
            {...props}
          />
          {loading && (
            <Loader2
              className={cn(
                "absolute h-4 w-4 animate-spin text-neutral-8",
                showClear ? "right-8" : "right-3"
              )}
            /> // Position spinner
          )}
          {showClear && (
            <Button
              type="button"
              variant="primary-ghost"
              size="icon"
              onClick={handleClear}
              className="absolute right-1 h-6 w-6 p-0 rounded-full text-neutral-8 hover:bg-neutral-3"
              disabled={disabled} // Only disable if explicitly passed as disabled
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {isDropdownOpen && (
          <Card className="absolute top-full w-[300px] right-0 mt-1 bg-white border border-[#E5E5E5] rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
            {results && results.length > 0 ? (
              <div className="py-2">
              {results.map((result) => (
                <div
                  key={result.id}
                  className="px-4 py-2 cursor-pointer hover:bg-primary-8 text-neutral-12"
                  onClick={() => handleResultClick(result)}
                >
                  {result.loanId ? (
                    <>
                      <div className="flex items-center gap-1 text-sm">
                        <span className="font-bold text-[#333] uppercase">
                          {highlightText(result.loanId, value)}
                        </span>
                        {result.loanType && (
                          <span className="text-[#333]">
                            , {highlightText(result.loanType, value)}
                          </span>
                        )}
                      </div>
                      <div className="text-[#555] text-xs mt-0.5">
                        {highlightText(result.propertyAddress, value)}
                      </div>
                    </>
                  ) : (
                    <div className="text-[#555] text-sm">
                      {highlightText(result.propertyAddress, value)}
                    </div>
                  )}
                </div>
              ))}
            </div>):(
            <div className="flex py-5 justify-center">
              No result found
             </div>
            )}
            
          </Card>
        )}
      </div>
    );
  }
);
SearchInput.displayName = "SearchInput";

export { SearchInput };
